import { createLocalAuthClient } from "./local-client";
import { createMsalAuthClient } from "./msal-client";

export interface AdminAccount {
  email: string;
  name?: string;
}

/**
 * One interface, two implementations:
 * - "msal": Microsoft Entra PKCE redirect flow; tokens carry the audience the
 *   backend verifies (src/lib/admin-oauth.ts).
 * - "local": no auth at all — the Vite dev proxy sends requests to the local
 *   backend, whose NODE_ENV=local bypass grants superadmin.
 *
 * Never import MSAL outside src/auth/ — pages only ever see this interface.
 */
export interface AuthClient {
  readonly mode: "msal" | "local";
  /** msal: initialize + handleRedirectPromise. Resolves before any render. */
  initialize(): Promise<void>;
  isAuthenticated(): boolean;
  getAccount(): AdminAccount | null;
  /**
   * msal: silent acquisition, redirecting to Microsoft when interaction is
   * required (the promise then never settles — the page navigates away).
   * local: always null.
   */
  getAccessToken(options?: { forceRefresh?: boolean }): Promise<string | null>;
  login(): Promise<void>;
  logout(): Promise<void>;
}

export const authClient: AuthClient =
  import.meta.env.VITE_AUTH_MODE === "local"
    ? createLocalAuthClient()
    : createMsalAuthClient();
